/**
 * Shared v3 liquid-glass chrome — Atmosphere, Reveal, Magnetic, PageHeader.
 *
 * Extracted from HomepageRedesign so every marketing sub-page (pricing,
 * case studies, contact, docs, blog, legal, 404) speaks the same visual
 * language: ambient mesh blobs, hairline grid, scroll reveals, magnetic
 * primary CTAs, and a consistent eyebrow + display title page header.
 */
import * as React from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { MEyebrow, MTitle, MLede } from "./_components";
import { GrainOverlay, LiquidGlassDefs } from "./LiquidGlass";

/* ----------------------------------------------------------------- */
/* Atmosphere — animated mesh blobs + bottom vignette                 */
/* ----------------------------------------------------------------- */
export function Atmosphere({ short = false }: { short?: boolean }) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <motion.div
        className="absolute -top-32 -left-24 h-[560px] w-[560px] rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--rd-accent-500) / 0.32) 0%, transparent 60%)",
        }}
        animate={{ x: [0, 70, -20, 0], y: [0, 40, -30, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-10 right-[-180px] h-[500px] w-[500px] rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--rd-accent-400) / 0.24) 0%, transparent 60%)",
        }}
        animate={{ x: [0, -50, 30, 0], y: [0, 30, -20, 0] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />
      {!short && (
        <motion.div
          className="absolute top-[460px] left-1/2 h-[380px] w-[380px] -translate-x-1/2 rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--rd-accent-300) / 0.20) 0%, transparent 60%)",
          }}
          animate={{ x: [-20, 30, -10, -20], y: [0, -20, 20, 0] }}
          transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
      {/* Bottom vignette to anchor */}
      <div
        className="absolute inset-x-0 bottom-0 h-48"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, hsl(var(--rd-canvas)) 100%)",
        }}
      />
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Reveal — scroll-triggered fade-up                                  */
/* ----------------------------------------------------------------- */
export function Reveal({
  children,
  delay = 0,
  y = 14,
  className,
}: React.PropsWithChildren<{ delay?: number; y?: number; className?: string }>) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -8% 0px" }}
      transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ----------------------------------------------------------------- */
/* Magnetic — gravity wrap for primary CTA                            */
/* ----------------------------------------------------------------- */
export function Magnetic({
  children,
  strength = 0.28,
}: React.PropsWithChildren<{ strength?: number }>) {
  const reduce = useReducedMotion();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 18, mass: 0.4 });

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * strength);
    y.set((e.clientY - r.top - r.height / 2) * strength);
  }
  function onLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={reduce ? undefined : { x: sx, y: sy }}
      className="inline-block"
    >
      {children}
    </motion.div>
  );
}

/* ----------------------------------------------------------------- */
/* GlassStage — reusable atmospheric backdrop wrapper for any page    */
/* ----------------------------------------------------------------- */
export function GlassStage({
  children,
  short = false,
  className,
}: React.PropsWithChildren<{ short?: boolean; className?: string }>) {
  return (
    <>
      <LiquidGlassDefs />
      <GrainOverlay opacity={0.035} />
      <div className={className}>
        <section className="relative overflow-hidden">
          <Atmosphere short={short} />
          {/* Hairline grid, masked toward edges */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 [background-image:linear-gradient(to_right,hsl(var(--rd-border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--rd-border))_1px,transparent_1px)] [background-size:48px_48px] opacity-[0.10] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
          />
          <div className="relative">{children}</div>
        </section>
      </div>
    </>
  );
}

/* ----------------------------------------------------------------- */
/* PageHeader — consistent hero header for sub-pages                  */
/* ----------------------------------------------------------------- */
export function PageHeader({
  eyebrow,
  title,
  lede,
  align = "center",
  children,
}: {
  eyebrow: React.ReactNode;
  title: React.ReactNode;
  lede?: React.ReactNode;
  align?: "left" | "center";
  children?: React.ReactNode;
}) {
  const isCenter = align === "center";
  return (
    <div
      className={
        "relative mx-auto w-full max-w-7xl px-6 pt-24 pb-16 md:pt-32 " +
        (isCenter ? "text-center" : "")
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <MEyebrow className={isCenter ? "" : ""}>{eyebrow}</MEyebrow>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: 0.08 }}
      >
        <MTitle className={"mt-5 " + (isCenter ? "mx-auto" : "")}>{title}</MTitle>
      </motion.div>

      {lede && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        >
          <MLede className={"mt-6 " + (isCenter ? "mx-auto" : "")}>{lede}</MLede>
        </motion.div>
      )}

      {children && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
          className={"mt-8 " + (isCenter ? "flex flex-wrap justify-center gap-3" : "")}
        >
          {children}
        </motion.div>
      )}
    </div>
  );
}
